import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Extension } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useUpdateExtension } from '../../lib/api/hooks.js';
import { useSession } from '../../lib/auth/session.js';
import { Field } from '../../components/ui/Form.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { relativeTime } from '../../lib/format.js';

/**
 * An extension's number: shown, not edited in place (owner, 19 September 2026). Changing it is the
 * provider's step, with what it costs said first: every phone on the seat signs in with a username
 * built from the number. Calls and spend stay with the extension, and each call keeps the number it
 * was made on (migration 0088), so the numbers it has had are listed under it.
 */

interface Held { number: string; from_at: string; to_at: string | null; changed_by: string | null }

const day = (iso: string) => new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });

export function ExtensionNumberField({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const can = useSession((s) => s.can);
  const qc = useQueryClient();
  const save = useUpdateExtension(tenantId);
  const [changing, setChanging] = useState(false);
  const [next, setNext] = useState('');
  const history = useQuery({
    queryKey: ['number-history', tenantId, ext.id],
    queryFn: () => api.get<{ data: Held[] }>(`/v1/tenants/${tenantId}/extensions/${ext.id}/number-history`),
  });
  const previous = (history.data?.data ?? []).filter((h) => h.to_at);
  const valid = /^[0-9]{2,6}$/.test(next) && next !== ext.number;

  return (
    <Field label="Extension number" htmlFor="d-number"
      hint="Shared dial space: hunt lists, queues and IVRs use the same range.">
      <div className="arc-row" style={{ gap: 10 }}>
        <span id="d-number" className="arc-mono" style={{ fontSize: 15, fontWeight: 600 }}>{ext.number}</span>
        {can('extension:renumber') && !changing && (
          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => { setNext(''); setChanging(true); }}>
            Change number
          </button>
        )}
      </div>

      {changing && (
        <div style={{ marginTop: 10, padding: 12, border: '1px solid var(--arc-warn-border)', background: 'var(--arc-warn-bg)', borderRadius: 'var(--arc-r-sm)' }}>
          <div style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 10 }}>
            Every phone on {ext.number} signs in with a username built from its number. Desk phones
            pick up the new one from their provisioning file; a softphone or app set up by hand stops
            registering until it is given the new username. Its calls and spend stay with it.
          </div>
          {save.error && <ErrorBanner error={save.error} />}
          <div className="arc-row" style={{ gap: 8 }}>
            <input className="arc-input arc-mono" style={{ maxWidth: 140 }} value={next} autoFocus
              aria-label="New number" placeholder="New number" inputMode="numeric"
              onChange={(e) => setNext(e.target.value.replace(/\D/g, '').slice(0, 6))} />
            <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!valid || save.isPending}
              onClick={() => {
                if (!confirm(`Change ${ext.number} to ${next}?`)) return;
                save.mutate({ id: ext.id, number: next }, {
                  onSuccess: () => {
                    setChanging(false);
                    void qc.invalidateQueries({ queryKey: ['number-history', tenantId, ext.id] });
                  },
                });
              }}>
              {save.isPending ? 'Changing…' : `Change to ${next || '…'}`}
            </button>
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => setChanging(false)}>Cancel</button>
          </div>
        </div>
      )}

      {previous.length > 0 && (
        <div className="arc-hint" style={{ marginTop: 6 }}>
          Previously {previous.map((h) => `${h.number} until ${day(h.to_at!)}`).join(', ')}.
          {history.data?.data[0]?.changed_by
            ? ` Changed by ${history.data.data[0].changed_by} ${relativeTime(previous[0]!.to_at!)}.` : ''}
        </div>
      )}
    </Field>
  );
}
